import type {
	InvoicesResponse,
	PaymentMethodResponse,
	SubscriptionDetailsResponse,
	SubscriptionPreviewUpgradeResponse,
	SubscriptionResponse,
} from '@kimuno/api-sdk/lib/types/responses/Billing';
import type {
	SchoolAdminListResponse,
	SchoolInvoicesResponse,
	SchoolSeatsOverviewResponse,
	TeacherListResponse,
} from '@kimuno/api-sdk/lib/types/responses/Teacher';
import type { ExperimentData, UserResponseData } from '@kimuno/api-sdk/lib/types/responses/User';
import immutable from 'object-path-immutable';

import {
	LOGIN,
	LOGOUT,
	type LoginAction,
	type LogoutAction,
	REFRESH_TOKEN,
	type RefreshTokenExpiredAction,
} from '../actions/auth.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
	CREATE_COURSE_REGISTRATION,
	type CreateCourseRegistrationAction,
} from '../actions/course.actionTypes';
import {
	FETCH_ME,
	type FetchMeAction,
	GET_EXPERIMENT,
	GET_INVOICES,
	GET_PAYMENT_METHOD,
	GET_SCHOOL_ADMINS,
	GET_SCHOOL_INVOICES,
	GET_SCHOOL_SEATS,
	GET_SCHOOL_TEACHERS,
	GET_STRIPE_SUBSCRIPTION_DETAILS,
	GET_SUBSCRIPTION,
	GET_SUBSCRIPTION_UPGRADE_PREVIEW,
	type GetExperimentAction,
	type GetInvoicesAction,
	type GetPaymentMethodAction,
	type GetSchoolAdminsAction,
	type GetSchoolInvoicesAction,
	type GetSchoolSeatsAction,
	type GetSchoolTeachersAction,
	type GetStripeSubscriptionDetailsAction,
	type GetSubscriptionAction,
	type GetSubscriptionUpgradePreviewAction,
	INCREMENT_DOWNLOADS_COUNTER,
	type IncrementDownloadsCounterAction,
	RECORD_TRIGGERED_HINT,
	REMOVE_TEACHER_FROM_STORE,
	type RecordTriggeredHintAction,
	type RemoveTeacherFromStoreAction,
	SET_SELECTED_SCHOOL,
	type SetSelectedSchoolAction,
	type SubmitRevocationAction,
	TRACK_EXPERIMENT,
	UPDATE_ME,
	UPDATE_NEWSLETTER,
	UPDATE_PAYMENT_METHOD,
	UPGRADE,
	type UpdateExperimentAction,
	type UpdateMeAction,
	type UpdateNewsletterAction,
	type UpdatePaymentMethodAction,
	type UpgradeAction,
} from '../actions/user.actionTypes';
import cookies from '../clients/cookies';
import { userType } from '../constants';

type UserActionTypes =
	| FetchMeAction
	| UpdateMeAction
	| UpdateNewsletterAction
	| RecordTriggeredHintAction
	| GetSubscriptionAction
	| GetSubscriptionUpgradePreviewAction
	| GetStripeSubscriptionDetailsAction
	| SubmitRevocationAction
	| GetPaymentMethodAction
	| UpdatePaymentMethodAction
	| GetInvoicesAction
	| GetSchoolInvoicesAction
	| LogoutAction
	| LoginAction
	| RefreshTokenExpiredAction
	| UpgradeAction
	| IncrementDownloadsCounterAction
	| GetExperimentAction
	| UpdateExperimentAction
	| CreateCourseRegistrationAction
	| GetSchoolTeachersAction
	| GetSchoolSeatsAction
	| RemoveTeacherFromStoreAction
	| GetSchoolAdminsAction
	| SetSelectedSchoolAction;

export interface UserInitialStateType {
	error: null | string;
	me?: UserResponseData;
	subscription?: SubscriptionResponse;
	subscriptionUpgradePreview?: SubscriptionPreviewUpgradeResponse;
	stripeSubscriptionDetails?: SubscriptionDetailsResponse;
	invoiceHistory?: InvoicesResponse;
	schoolInvoiceHistory?: SchoolInvoicesResponse;
	teachersList?: TeacherListResponse;
	schoolSeats?: SchoolSeatsOverviewResponse;
	schoolAdmins?: SchoolAdminListResponse;
	subscriptionStatus?: AsyncActionStatus;
	status: AsyncActionStatus;
	paymentMethod?: PaymentMethodResponse;
	experiments: {
		[key: string]: ExperimentData;
	};
	selectedSchoolId?: string;
	isAuthenticated: boolean;
}

const initialState: UserInitialStateType = {
	error: null,
	status: AsyncActionStatus.LOADING,
	subscriptionStatus: AsyncActionStatus.LOADING,
	experiments: {},
	isAuthenticated: !!cookies.getAuthToken() || !!cookies.getRefreshToken(),
};

const userIsDefined = (
	state: UserInitialStateType
): state is UserInitialStateType & { me: UserResponseData } => !!state.me;

// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
const user = (state = initialState, action: UserActionTypes): UserInitialStateType => {
	switch (action.type) {
		case UPDATE_ME.START:
		case FETCH_ME.START:
			return immutable(state).set('status', AsyncActionStatus.LOADING).value();
		case UPDATE_ME.ERROR:
		case FETCH_ME.ERROR:
		case GET_PAYMENT_METHOD.ERROR:
		case UPDATE_PAYMENT_METHOD.ERROR:
			return immutable(state)
				.set('status', AsyncActionStatus.ERROR)
				.set('error', action.error.message)
				.value();
		case FETCH_ME.EXPIRED:
			return immutable(state).set('me', undefined).set('experiments', {}).value();
		case UPDATE_ME.SUCCESS:
		case FETCH_ME.SUCCESS:
			return immutable(state)
				.set('status', AsyncActionStatus.READY)
				.set('me', {
					...action.result,
				})
				.value();
		case GET_SUBSCRIPTION.SUCCESS: {
			const { status = AsyncActionStatus.READY } = action.payload;

			return immutable(state)
				.set('subscriptionStatus', status)
				.set('subscription', action.result)
				.value();
		}
		case GET_SUBSCRIPTION_UPGRADE_PREVIEW.SUCCESS: {
			return immutable(state).set('subscriptionUpgradePreview', action.result).value();
		}
		case GET_STRIPE_SUBSCRIPTION_DETAILS.SUCCESS: {
			return immutable(state).set('stripeSubscriptionDetails', action.result).value();
		}
		case GET_SUBSCRIPTION_UPGRADE_PREVIEW.ERROR:
			return immutable(state)
				.set('status', AsyncActionStatus.ERROR)
				.set('error', action.error.message)
				.value();
		case GET_PAYMENT_METHOD.SUCCESS:
		case UPDATE_PAYMENT_METHOD.SUCCESS:
			return immutable(state)
				.set('status', AsyncActionStatus.READY)
				.set('paymentMethod', action.result)
				.value();
		case GET_INVOICES.SUCCESS:
			return immutable(state).set('invoiceHistory', action.result).value();
		case GET_SCHOOL_INVOICES.SUCCESS:
			return immutable(state).set('schoolInvoiceHistory', action.result).value();
		case GET_SCHOOL_TEACHERS.SUCCESS:
			return immutable(state).set('teachersList', action.result).value();
		case GET_SCHOOL_ADMINS.SUCCESS:
			return immutable(state).set('schoolAdmins', action.result).value();
		case LOGIN.START:
		case LOGOUT.START:
		case REFRESH_TOKEN.EXPIRED:
			return immutable(initialState).set('isAuthenticated', false).value();
		case LOGIN.SUCCESS:
			return immutable(state).set('isAuthenticated', true).value();
		case UPGRADE.START:
		case GET_PAYMENT_METHOD.START:
		case UPDATE_PAYMENT_METHOD.START:
			return immutable(state).set('status', AsyncActionStatus.LOADING).value();
		case UPGRADE.SUCCESS:
			return immutable(state)
				.set('status', AsyncActionStatus.READY)
				.set('me', {
					...action.result,
				})
				.value();
		case INCREMENT_DOWNLOADS_COUNTER: {
			if (!userIsDefined(state)) {
				return state;
			}
			return immutable(state)
				.update(['me', 'downloads', 'counter'], (value) => value + 1)
				.value();
		}
		case UPDATE_NEWSLETTER.START: {
			if (!userIsDefined(state)) {
				return state;
			}
			const newsletterData = {
				status: action.payload.optIn,
				doubleOptIn: action.payload.optIn ? 'confirmed' : ('rejected' as 'confirmed' | 'rejected'),
				updated: Date.now().toString(),
			};
			return immutable(state)
				.set(['me', 'marketing', 'mailings', 'newsletter'], newsletterData)
				.value();
		}
		case UPDATE_NEWSLETTER.ERROR: {
			if (!userIsDefined(state)) {
				return state;
			}
			const newsletterData = {
				status: !action.payload.optIn,
				doubleOptIn: !action.payload.optIn ? 'confirmed' : ('rejected' as 'confirmed' | 'rejected'),
				updated: Date.now().toString(),
			};
			return immutable(state)
				.set(['me', 'marketing', 'mailings', 'newsletter'], newsletterData)
				.value();
		}
		case RECORD_TRIGGERED_HINT.START: {
			if (!userIsDefined(state)) {
				return state;
			}
			const existing = state.me.data?.triggeredHints ?? [];
			if (existing.includes(action.payload.hint)) {
				return state;
			}
			// Optimistic: show the hint as seen immediately so it never double-fires
			// while the record request is in flight.
			return immutable(state)
				.set(['me', 'data', 'triggeredHints'], [...existing, action.payload.hint])
				.value();
		}
		case RECORD_TRIGGERED_HINT.SUCCESS: {
			if (!userIsDefined(state)) {
				return state;
			}
			// Union with optimistic state so an out-of-order response can't drop a key.
			const existing = state.me.data?.triggeredHints ?? [];
			const merged = Array.from(new Set([...existing, ...action.result.triggeredHints]));
			return immutable(state).set(['me', 'data', 'triggeredHints'], merged).value();
		}
		// NOTE: no RECORD_TRIGGERED_HINT.ERROR case on purpose — on failure we keep
		// the optimistic entry (the hint stays suppressed for the session) and let
		// the next fetchMe reconcile. Re-recording is idempotent server-side.
		case TRACK_EXPERIMENT.SUCCESS:
		case GET_EXPERIMENT.SUCCESS: {
			if (!userIsDefined(state) || !action.result.name) {
				return state;
			}
			return immutable(state).set(['experiments', action.result.name], action.result).value();
		}

		case CREATE_COURSE_REGISTRATION.SUCCESS: {
			if (state.me?.didReachCoursesLimit || state.me?.type !== userType.document) return state;

			return immutable(state).merge(['me'], { didReachCoursesLimit: true }).value();
		}

		case CREATE_COURSE_REGISTRATION.ERROR: {
			if (
				(action.error as Error & { data: { code: string } }).data?.code ===
				'COURSE_REGISTRATION_LIMIT_REACHED'
			) {
				return immutable(state).merge(['me'], { didReachCoursesLimit: true }).value();
			}

			return state;
		}

		case GET_SCHOOL_SEATS.START:
			return immutable(state).set('status', AsyncActionStatus.LOADING).value();

		case GET_SCHOOL_SEATS.SUCCESS:
			return immutable(state)
				.set('status', AsyncActionStatus.READY)
				.set('schoolSeats', action.result)
				.value();

		case GET_SCHOOL_SEATS.ERROR:
			return immutable(state)
				.set('status', AsyncActionStatus.ERROR)
				.set('error', action.error.message)
				.value();

		case REMOVE_TEACHER_FROM_STORE.REMOVE: {
			if (!state.teachersList) {
				return state;
			}

			const updatedTeachers = state.teachersList.teachers.filter(
				(teacher) => teacher.userId !== action.payload.userId
			);

			return immutable(state)
				.set('teachersList', {
					...state.teachersList,
					teachers: updatedTeachers,
					size: state.teachersList.size - 1,
				})
				.value();
		}

		case SET_SELECTED_SCHOOL.ACTION: {
			const setSchoolAction = action as SetSelectedSchoolAction;
			return immutable(state)
				.set('selectedSchoolId', setSchoolAction.payload.schoolId)
				.set('schoolSeats', undefined) // Clear school seats when switching schools
				.value();
		}
	}

	return state;
};

export default user;
